import { type PropsWithChildren } from 'react';

import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { defaultUnitFormatter } from '../../core/utils/defaultUnitFormatter.js';
import {
  getFormattedTicks,
  getFormatterByTicks,
} from '../../core/utils/formatter-by-ticks.js';
import {
  getHorizontalNumTicks,
  getVerticalNumTicks,
} from '../../core/utils/ticks.js';
import { HistogramTickAxisContext } from '../contexts/HistogramTickAxis.context.js';
import { HistogramXFormatterContext } from '../contexts/HistogramXFormatter.context.js';
import { HistogramYFormatterContext } from '../contexts/HistogramYFormatter.context.js';
import { useHistogramFilteredData } from '../hooks/useHistogramFilteredData.js';
import { useHistogramValueRepresentation } from '../hooks/useHistogramValueRepresentation.js';
import { useHistogramXScale } from '../hooks/useHistogramXScale.js';
import { useHistogramYScales } from '../hooks/useHistogramYScales.js';
import { useXAxisConfig } from '../hooks/useXAxisConfig.js';
import { useYAxisConfig } from '../hooks/useYAxisConfig.js';

export const HistogramFormatterProvider = ({ children }: PropsWithChildren) => {
  const { height, width } = useGridPlotSize();
  const [leftScale, rightScale] = useHistogramYScales();
  const { xScale } = useHistogramXScale();
  const yAxes = useYAxisConfig();
  const { unit: bottomUnit, formatter: bottomAxisFormatter } = useXAxisConfig();
  const {
    units: [leftUnit, rightUnit],
  } = useHistogramFilteredData();

  const verticalNumTicks = getVerticalNumTicks(height);
  const horizontalNumTicks = getHorizontalNumTicks(width);
  const valueRepresentation = useHistogramValueRepresentation();

  const leftFormatter = getFormatterByTicks(
    verticalNumTicks,
    leftScale,
    leftUnit,
    valueRepresentation === 'relative'
      ? defaultUnitFormatter(leftUnit)
      : yAxes.left?.formatter,
  );
  const rightFormatter = getFormatterByTicks(
    verticalNumTicks,
    rightScale,
    rightUnit,
    valueRepresentation === 'relative'
      ? defaultUnitFormatter(rightUnit)
      : yAxes.right?.formatter,
  );

  const bottomFormatter = getFormatterByTicks(
    horizontalNumTicks,
    xScale,
    bottomUnit,
    bottomAxisFormatter,
  );

  const leftTicks = getFormattedTicks(
    verticalNumTicks,
    leftScale,
    leftUnit,
    leftFormatter,
  );

  const rightTicks = getFormattedTicks(
    verticalNumTicks,
    rightScale,
    rightUnit,
    rightFormatter,
  );

  const bottomTicks = getFormattedTicks(
    horizontalNumTicks,
    xScale,
    bottomUnit,
    bottomFormatter,
  );

  return (
    <HistogramYFormatterContext.Provider
      value={[leftFormatter, rightFormatter]}
    >
      <HistogramXFormatterContext.Provider value={bottomFormatter}>
        <HistogramTickAxisContext.Provider
          value={{ left: leftTicks, right: rightTicks, bottom: bottomTicks }}
        >
          {children}
        </HistogramTickAxisContext.Provider>
      </HistogramXFormatterContext.Provider>
    </HistogramYFormatterContext.Provider>
  );
};

HistogramFormatterProvider['displayName'] = 'HistogramFormatterProvider';
